import { useState, useMemo } from 'react'
import { Badge } from '#app/components/ui/badge.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Input } from '#app/components/ui/input.tsx'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '#app/components/ui/table.tsx'
import {
	DFW_CITY_PERMITS,
	GENERAL_PERMIT_NOTES,
	type CityPermitInfo,
} from '../dumpster-permit-data.ts'

function PermitBadge({ status }: { status: CityPermitInfo['status'] }) {
	if (status === 'special') {
		return (
			<Badge className="bg-red-100 text-red-700 hover:bg-red-100 dark:bg-red-950/50 dark:text-red-300">
				Franchise Rule
			</Badge>
		)
	}
	return (
		<Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 dark:bg-amber-950/50 dark:text-amber-300">
			Required (ROW)
		</Badge>
	)
}

export function DumpsterPermitGuide() {
	const [search, setSearch] = useState('')

	const filteredCities = useMemo(() => {
		if (!search.trim()) return DFW_CITY_PERMITS
		const q = search.toLowerCase().trim()
		return DFW_CITY_PERMITS.filter((c) => c.city.toLowerCase().includes(q))
	}, [search])

	return (
		<section>
			<Badge variant="secondary" className="mb-3">
				Permit Guide
			</Badge>
			<h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
				DFW Dumpster Permit Requirements
			</h2>
			<p className="text-muted-foreground mt-2">
				Before placing a roll-off container on public property, many cities
				require a permit. Find your city below.
			</p>

			{/* Universal rule */}
			<div className="mt-8 grid gap-4 sm:grid-cols-2">
				<div className="rounded-lg border border-green-200 bg-green-50 p-5 dark:border-green-800 dark:bg-green-950/30">
					<div className="flex items-start gap-3">
						<div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/50">
							<Icon
								name="check"
								size="sm"
								className="text-green-600 dark:text-green-400"
							/>
						</div>
						<div>
							<h3 className="text-sm font-semibold text-green-800 dark:text-green-300">
								No Permit Needed
							</h3>
							<p className="text-muted-foreground mt-1 text-sm">
								Dumpster on{' '}
								<strong className="text-foreground">
									private property
								</strong>{' '}
								— your driveway, a job site lot, a private parking
								area, or any land you own or have permission to use.
							</p>
						</div>
					</div>
				</div>
				<div className="rounded-lg border border-amber-200 bg-amber-50 p-5 dark:border-amber-800 dark:bg-amber-950/30">
					<div className="flex items-start gap-3">
						<div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/50">
							<Icon
								name="alert-triangle"
								size="sm"
								className="text-amber-600 dark:text-amber-400"
							/>
						</div>
						<div>
							<h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300">
								Permit Required
							</h3>
							<p className="text-muted-foreground mt-1 text-sm">
								Dumpster on{' '}
								<strong className="text-foreground">
									public property
								</strong>{' '}
								— a city street, sidewalk, alley, or any public
								right-of-way. A ROW permit is required in
								essentially every DFW city.
							</p>
						</div>
					</div>
				</div>
			</div>

			{/* General notes */}
			<div className="mt-6 rounded-lg border border-blue-200 bg-blue-50 p-5 dark:border-blue-800 dark:bg-blue-950/30">
				<h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-blue-700 dark:text-blue-300">
					Important Notes for All Cities
				</h3>
				<ul className="space-y-1.5">
					{GENERAL_PERMIT_NOTES.map((note, i) => (
						<li
							key={i}
							className="text-muted-foreground flex items-start gap-2 text-sm"
						>
							<span className="mt-0.5 shrink-0 text-blue-500">
								&rarr;
							</span>
							{note}
						</li>
					))}
				</ul>
			</div>

			{/* Search + table */}
			<div className="mt-8">
				<div className="mb-4 max-w-sm">
					<Input
						placeholder="Search city..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="h-10"
					/>
				</div>

				<div className="overflow-hidden rounded-lg border">
					<div className="overflow-x-auto">
						<Table>
							<TableHeader>
								<TableRow className="bg-muted/50">
									<TableHead className="w-40">City</TableHead>
									<TableHead className="w-32">
										Street Permit?
									</TableHead>
									<TableHead>
										Permit Details & Contact
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{filteredCities.length > 0 ? (
									filteredCities.map((city) => (
										<TableRow key={city.city}>
											<TableCell className="font-semibold">
												{city.city}
											</TableCell>
											<TableCell>
												<PermitBadge
													status={city.status}
												/>
											</TableCell>
											<TableCell>
												<p className="text-muted-foreground text-sm">
													{city.details}
												</p>
												<span className="text-muted-foreground mt-1.5 inline-flex items-center gap-1.5 rounded border bg-muted/50 px-2 py-0.5 text-xs">
													<Icon
														name="phone"
														className="size-3"
													/>
													{city.contact}
													{city.website && (
														<>
															{' | '}
															{city.website}
														</>
													)}
												</span>
											</TableCell>
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell
											colSpan={3}
											className="py-8 text-center"
										>
											<p className="text-muted-foreground">
												No cities found matching &ldquo;
												{search}&rdquo;. Try a different
												search or contact your city
												directly.
											</p>
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</div>
				</div>

				{/* Disclaimer */}
				<p className="text-muted-foreground mt-4 text-xs leading-relaxed">
					<strong className="text-foreground">Disclaimer:</strong> Permit
					requirements, fees, and contact information are subject to
					change. This guide reflects research current as of early 2026
					and is intended as a general reference only. Always verify
					requirements directly with your city&apos;s Public Works or
					Engineering Department before scheduling a dumpster delivery.
				</p>
			</div>
		</section>
	)
}
